<template>
  <app-dropdown-button>
    <template #left-icon>
      <app-material-icon
        name="add_2"
        color="inherit"
      />
    </template>
    {{ $t('common.add') }}

    <template #list>
      <v-list data-locator="checklist-add-menu">
        <app-dropdown-button-item
          v-if="!createTaskPending && !isLastSectionAwaitingDelete"
          data-locator="add-task-menu-item"
          icon="check_box"
          :label="t('addTask')"
          @click="createTask"
        />

        <v-list-item
          v-else
          data-locator="add-task-menu-item"
          class="text-greyDark cursor-default"
        >
          <AppTooltip>
            <template #activator="{ props }">
              <v-list-item-title v-bind="props" class="dropdown-item">
                <div class="icon-wrapper">
                  <app-material-icon
                    name="check_box"
                    color="inherit"
                  />
                </div>
                <span>{{ t('addTask') }}</span>
              </v-list-item-title>
            </template>
            {{ t('addTaskDisableTooltip') }}
          </AppTooltip>
        </v-list-item>

        <app-dropdown-button-item
          data-locator="add-section-menu-item"
          :disabled="createSectionPending"
          icon="calendar_view_day"
          :label="t('addSection')"
          @click="createSection"
        />

        <template v-if="isSubscriber">
          <v-divider />

          <app-dropdown-button-item
            data-locator="template-import-menu-item"
            icon="team_dashboard"
            :label="t('addFromTemplate')"
            @click="onImportChecklistFromTemplate"
          />

          <app-dropdown-button-item
            data-locator="excel-import-menu-item"
            :label="t('importFromExcel')"
            @click="onImportChecklistClick"
          >
            <template #icon>
              <OfficeIcon
                class="excel-icon"
                document-type="excel"
                :height="16"
                icon-type="product"
                :width="16"
              />
            </template>
          </app-dropdown-button-item>

          <app-dropdown-button-item
            data-locator="smart-import-menu-item"
            icon="file-word"
            :label="t('smartImport')"
            tag="beta"
            @click="onSmartImportClick"
            v-if="hasAccessToSmartImport"
          >
            <template #icon>
              <OfficeIcon
                class="word-icon"
                document-type="word"
                :height="16"
                icon-type="product"
                :width="16"
              />
            </template>
          </app-dropdown-button-item>
        </template>
      </v-list>
    </template>
  </app-dropdown-button>
</template>

<script lang="ts">
import { defineComponent } from 'vue'
import { mapActions, mapGetters, mapMutations, mapState } from 'vuex'

import AppTooltip from '@/common/AppTooltip.vue'
import OfficeIcon from '@/common/office/OfficeIcon.vue'
import { toast } from '@/common/utils/toast'
import { useTranslation } from '@/common/utils/translation'
import { track } from '@/common/pendo/agent'
import { CREATE_TASK, CREATE_SECTION } from '@/store/modules/checklist/action_types'
import {
  SET_IMPORT_CHECKLIST_DIALOG_IS_OPEN,
  SET_IMPORT_CHECKLIST_FROM_TEMPLATE_DIALOG_IS_OPEN,
  SET_SMART_IMPORT_MODAL_IS_OPEN,
} from '@/store/modules/checklist/mutation_types'

export default defineComponent({
  name: 'ChecklistAddButton',
  components: { AppTooltip, OfficeIcon },
  setup () {
    return useTranslation({ keyPrefix: 'project.checklist.ChecklistAddButton' })
  },
  computed: {
    ...mapState('checklist', ['createSectionPending', 'createTaskPending']),
    ...mapState('user', ['profile']),
    ...mapGetters('checklist', ['flatChecklist']),
    ...mapGetters('user', ['isSubscriber']),
    ...mapGetters('room', ['roomMnemo']),
    hasAccessToSmartImport () {
      return this.profile.subscription?.accessSmartTodoImport ?? false
    },
    isLastSectionAwaitingDelete (): boolean {
      const lastSection = this.flatChecklist.findLast((item: any) => item.type === 'title')
      return lastSection?.isAwaitingValidation === true
    },
  },
  methods: {
    ...mapActions('checklist', [CREATE_TASK, CREATE_SECTION]),
    ...mapMutations('checklist', [
      SET_IMPORT_CHECKLIST_DIALOG_IS_OPEN,
      SET_IMPORT_CHECKLIST_FROM_TEMPLATE_DIALOG_IS_OPEN,
      SET_SMART_IMPORT_MODAL_IS_OPEN,
    ]),
    async createTask () {
      try {
        const data = {
          prevId: null,
          prevType: null,
        }
        if (this.flatChecklist.length > 0) {
          const lastElement = this.flatChecklist[this.flatChecklist.length - 1]
          data.prevId = lastElement.id
          data.prevType = lastElement.type
        }
        await this.CREATE_TASK({
          mnemo: this.roomMnemo,
          data,
        })
        toast.success(this.t('createTaskSuccess'))
      } catch (e) {
        toast.error(this.t('createTaskError'))
      }
    },
    async createSection () {
      try {
        const data = {
          title: '',
          depth: 0,
          prevId: null,
          prevType: null,
        }
        if (this.flatChecklist.length > 0) {
          const lastElement = this.flatChecklist[this.flatChecklist.length - 1]
          data.prevId = lastElement.id
          data.prevType = lastElement.type
        }
        await this.CREATE_SECTION({
          mnemo: this.roomMnemo,
          data,
        })
        toast.success(this.t('createSectionSuccess'))
      } catch (e) {
        toast.error(this.t('createSectionError'))
      }
    },
    onImportChecklistFromTemplate () {
      track('checklist_import_started', { type: 'template', project_mnemo: this.roomMnemo })
      this.SET_IMPORT_CHECKLIST_FROM_TEMPLATE_DIALOG_IS_OPEN(true)
    },
    onImportChecklistClick () {
      track('checklist_import_started', { type: 'excel', project_mnemo: this.roomMnemo })
      this.SET_IMPORT_CHECKLIST_DIALOG_IS_OPEN(true)
    },
    onSmartImportClick () {
      track('checklist_import_started', { type: 'word', project_mnemo: this.roomMnemo })
      this.SET_SMART_IMPORT_MODAL_IS_OPEN(true)
    },
  },
})
</script>

<style scoped lang="scss">
.dropdown-item {
  display: flex;
  align-items: center;
  column-gap: 8px;

  .icon-wrapper {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 20px;
  }
}
</style>
